import React, { Dispatch, SetStateAction, useMemo } from 'react'

// ** MUI Imports
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

// ** Third Party Imports
import { addDays, addMonths, isBefore, isAfter, min as dateMin, getMonth, getYear, startOfMonth, endOfMonth } from 'date-fns'
import { DateRangePicker, RangeKeyDict } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import { getStaticRanges } from 'src/assets/AnalyticsConfig'
import { IconButton } from '@mui/material'
import { Close } from 'mdi-material-ui'
import { SelectionRangeType, StaticRangeType } from 'src/lib/types/filters'

interface DatePickerDialogProps {
  showDatePicker: boolean
  setShowDatePicker: Dispatch<SetStateAction<boolean>>
  dateRange: SelectionRangeType
  handleSelect: (ranges: RangeKeyDict) => void
  minDateValue?: Date
  maxDateValue?: Date
  setSelectedStaticRange: Dispatch<SetStateAction<StaticRangeType>>
  handleApply: () => void
  jobButtonText?: string
  excludeStaticRanges?: string[]
  restrictMonthNavigation?: boolean
}

const DatePickerDialog: React.FC<DatePickerDialogProps> = ({
  showDatePicker,
  setShowDatePicker,
  dateRange,
  handleSelect,
  minDateValue,
  maxDateValue,
  setSelectedStaticRange,
  handleApply,
  jobButtonText = 'Apply',
  excludeStaticRanges,
  restrictMonthNavigation
}) => {
  const minLookbackDate = useMemo(() => addDays(new Date(), -90), [])

  const effectiveMaxDate = useMemo(() => {
    const base = maxDateValue ?? new Date()
    if (dateRange.startDate) {
      return dateMin([addMonths(dateRange.startDate, 3), base])
    }
    return base
  }, [dateRange.startDate, maxDateValue])

  return (
    <Dialog
      open={showDatePicker}
      disableRestoreFocus
      onClose={() => {
        setShowDatePicker(false)
      }}
      maxWidth='lg'
      PaperProps={{
        style: { borderRadius: 8, padding: '1rem', display: 'flex', gap: '1rem' }
      }}
      style={{ zIndex: 10000 }}
    >
      <DialogTitle
        color='black'
        variant='h5'
        sx={{
          padding: '0rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        Date filters
        <IconButton
          aria-label='close'
          onClick={() => {
            setShowDatePicker(false)
          }}
        >
          <Close color={'secondary'} sx={{ transform: 'scale(1.3)' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '0rem' }}>
        <DateRangePicker
          ranges={[dateRange]}
          onChange={handleSelect}
          months={2}
          editableDateInputs
          minDate={minDateValue ?? minLookbackDate}
          maxDate={effectiveMaxDate}
          disabledDay={day => isBefore(day, minDateValue ?? minLookbackDate) || isAfter(day, effectiveMaxDate)}
          direction={'horizontal'}
          staticRanges={getStaticRanges(setSelectedStaticRange).filter(r => !excludeStaticRanges?.includes(r.label))}
          {...(restrictMonthNavigation
            ? {
                navigatorRenderer: (focusedDate: Date, changeShownDate: (value: number | string | Date, mode?: 'set' | 'setYear' | 'setMonth' | 'monthOffset') => void) => {
                  const effectiveMin = minDateValue ?? minLookbackDate
                  const effectiveMax = maxDateValue ?? new Date()
                  const minMonth = getMonth(effectiveMin)
                  const maxMonth = getMonth(effectiveMax)
                  const minYear = getYear(effectiveMin)
                  const maxYear = getYear(effectiveMax)
                  const focusedYear = getYear(focusedDate)
                  const months = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                  ]
                  return (
                    <div
                      onMouseUp={(e: React.MouseEvent) => e.stopPropagation()}
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}
                    >
                      <button
                        type='button'
                        className='rdrPprevButton rdrNextPrevButton'
                        onClick={() => changeShownDate(-1, 'monthOffset')}
                      >
                        <i />
                      </button>
                      <span className='rdrMonthAndYearPickers'>
                        <span className='rdrMonthPicker'>
                          <select
                            value={getMonth(focusedDate)}
                            onChange={e => changeShownDate(e.target.value, 'setMonth')}
                          >
                            {months.map((name, i) => {
                              const disabled =
                                (focusedYear === minYear && i < minMonth) ||
                                (focusedYear === maxYear && i > maxMonth)
                              return (
                                <option key={i} value={i} disabled={disabled}>
                                  {name}
                                </option>
                              )
                            })}
                          </select>
                        </span>
                        <span className='rdrMonthAndYearDivider' />
                        <span className='rdrYearPicker'>
                          <select
                            value={focusedYear}
                            onChange={e => changeShownDate(e.target.value, 'setYear')}
                          >
                            {Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i).map(year => (
                              <option key={year} value={year}>
                                {year}
                              </option>
                            ))}
                          </select>
                        </span>
                      </span>
                      <button
                        type='button'
                        className='rdrNextButton rdrNextPrevButton'
                        onClick={() => changeShownDate(1, 'monthOffset')}
                      >
                        <i />
                      </button>
                    </div>
                  )
                }
              }
            : {})}
          inputRanges={[]}
        />
      </DialogContent>
      <DialogActions sx={{ padding: '0rem' }}>
        <Button
          variant='text'
          color={'secondary'}
          onClick={() => {
            setShowDatePicker(false)
          }}
        >
          Cancel
        </Button>
        <Button variant={'contained'} onClick={handleApply}>
          {jobButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DatePickerDialog
